import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import TableEdit from './TableEdit';
import TableInfo from './TableInfo';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useSystemFeatures } from '../hooks/useSystemFeatures';

function TableDisplay({ tables, theme, onOrderSelect, tableInfo, setTableInfo, user, readOnly = false, floorPlanWidth = 600, floorPlanHeight = 400, gridSnapSize = 0, onFloorPlanResize }) {
    const { t } = useLanguage();
    const { tableAreasEnabled } = useSystemFeatures();
    const [orders, setOrders] = useState([]);
    const [editingTable, setEditingTable] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [areas, setAreas] = useState([]);
    const [selectedAreaId, setSelectedAreaId] = useState(null); // null shows every area
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // Fetch tables from backend
    async function refreshTables() {
        try {
            const response = await fetch(`${BACKEND_URL}/api/tables`);
            const data = await response.json();
            setTableData(data);
        } catch (error) {
            console.error('Error fetching tables:', error);
        }
    }
    useEffect(() => {
        refreshTables();
        window.refreshTables = refreshTables;
        async function fetchOrders() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/orders`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch orders');
                const data = await response.json();
                setOrders(data);
            } catch (err) {
                setOrders([]);
            }
        }
        fetchOrders();
        return () => { window.refreshTables = undefined; };
    }, [t]);

    // Measure container width for scaling
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [editingTable, tableInfo]);

    // Load the areas the floor plan can be filtered by
    useEffect(() => {
        if (!tableAreasEnabled) return;
        fetch(`${BACKEND_URL}/api/table-areas`)
            .then(res => (res.ok ? res.json() : []))
            .then(data => setAreas(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching table areas:', err));
    }, [tableAreasEnabled]);

    // Only show tables where temporary === 0 and deleted === 0, and in the
    // selected area when the tenant uses areas
    const filteredTables = useMemo(() => {
        return (tableData.length ? tableData : tables)
            .filter(table => table.deleted === 0 && table.temporary === 0)
            .filter(table => !tableAreasEnabled || selectedAreaId === null || (table.area_id || 1) === selectedAreaId);
    }, [tableData, tables, tableAreasEnabled, selectedAreaId]);

    // Local state for positions
    const [positions, setPositions] = useState({});

    useEffect(() => {
        setPositions(
            filteredTables.reduce((acc, table) => {
                acc[table.id] = { x: table.x, y: table.y };
                return acc;
            }, {})
        );
    }, [filteredTables]);

    // Calculate minimum canvas size from current table positions
    const minCanvasFromTables = useMemo(() => {
        let minW = 200, minH = 200;
        filteredTables.forEach(table => {
            const pos = positions[table.id] || { x: table.x, y: table.y };
            const right = pos.x + (table.width || 40) + 10;
            const bottom = pos.y + (table.height || 40) + 10;
            if (right > minW) minW = right;
            if (bottom > minH) minH = bottom;
        });
        return { width: Math.ceil(minW), height: Math.ceil(minH) };
    }, [filteredTables, positions]);

    // Canvas dimensions and scaling
    const gridPadding = 5;
    const canvasWidth = floorPlanWidth || 600;
    const canvasHeight = floorPlanHeight || 400;

    // Snap-to-grid toggle (persisted in localStorage)
    const [snapEnabled, setSnapEnabled] = useState(() => {
        const stored = localStorage.getItem('tableDisplaySnapEnabled');
        return stored !== null ? stored === 'true' : true;
    });
    useEffect(() => {
        localStorage.setItem('tableDisplaySnapEnabled', snapEnabled);
    }, [snapEnabled]);

    // Drag logic
    const dragInfo = useRef({ id: null, offsetX: 0, offsetY: 0 });
    const dragTimeout = useRef(null);
    const isDragging = useRef(false);
    const [shakingId, setShakingId] = useState(null);
    const [movementEnabled, setMovementEnabled] = useState(false);

    // Edge resize state
    const resizeInfo = useRef({ edge: null, startX: 0, startY: 0, startWidth: 0, startHeight: 0, latestWidth: null, latestHeight: null });
    const [resizingEdge, setResizingEdge] = useState(null);
    const [localCanvasWidth, setLocalCanvasWidth] = useState(null);
    const [localCanvasHeight, setLocalCanvasHeight] = useState(null);
    const onFloorPlanResizeRef = useRef(onFloorPlanResize);
    useEffect(() => { onFloorPlanResizeRef.current = onFloorPlanResize; }, [onFloorPlanResize]);

    // Use local overrides during resize, otherwise use props
    const effectiveCanvasWidth = localCanvasWidth !== null ? localCanvasWidth : canvasWidth;
    const effectiveCanvasHeight = localCanvasHeight !== null ? localCanvasHeight : canvasHeight;
    const effectiveGridWidth = effectiveCanvasWidth;
    const effectiveGridHeight = effectiveCanvasHeight;
    // Scale to fill container (can scale up or down)
    const effectiveScale = containerWidth > 0 ? (containerWidth / effectiveGridWidth) : 1;
    const effectiveScaledHeight = effectiveGridHeight * effectiveScale;

    // Edge resize handlers
    const handleResizeStart = useCallback((e, edge) => {
        e.preventDefault();
        e.stopPropagation();
        const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
        resizeInfo.current = {
            edge,
            startX: clientX,
            startY: clientY,
            startWidth: effectiveCanvasWidth,
            startHeight: effectiveCanvasHeight,
            latestWidth: effectiveCanvasWidth,
            latestHeight: effectiveCanvasHeight
        };
        setResizingEdge(edge);

        const handleResizeMove = (e) => {
            const cx = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
            const cy = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
            const info = resizeInfo.current;
            const currentScale = containerWidth > 0 ? (containerWidth / info.startWidth) : 1;

            if (info.edge === 'right' || info.edge === 'corner') {
                const deltaX = (cx - info.startX) / currentScale;
                const newW = Math.max(minCanvasFromTables.width, Math.min(2000, Math.round(info.startWidth + deltaX)));
                info.latestWidth = newW;
                setLocalCanvasWidth(newW);
            }
            if (info.edge === 'bottom' || info.edge === 'corner') {
                const deltaY = (cy - info.startY) / currentScale;
                const newH = Math.max(minCanvasFromTables.height, Math.min(2000, Math.round(info.startHeight + deltaY)));
                info.latestHeight = newH;
                setLocalCanvasHeight(newH);
            }
            if (e.type === 'touchmove') e.preventDefault();
        };

        const handleResizeEnd = () => {
            setResizingEdge(null);
            window.removeEventListener('mousemove', handleResizeMove);
            window.removeEventListener('mouseup', handleResizeEnd);
            window.removeEventListener('touchmove', handleResizeMove);
            window.removeEventListener('touchend', handleResizeEnd);

            const finalWidth = resizeInfo.current.latestWidth;
            const finalHeight = resizeInfo.current.latestHeight;

            if (onFloorPlanResizeRef.current) {
                onFloorPlanResizeRef.current(finalWidth, finalHeight);
            }

            setTimeout(() => {
                setLocalCanvasWidth(null);
                setLocalCanvasHeight(null);
            }, 100);
        };

        window.addEventListener('mousemove', handleResizeMove);
        window.addEventListener('mouseup', handleResizeEnd);
        window.addEventListener('touchmove', handleResizeMove, { passive: false });
        window.addEventListener('touchend', handleResizeEnd);
    }, [effectiveCanvasWidth, effectiveCanvasHeight, containerWidth, minCanvasFromTables]);

    function handleDrag(e) {
        if (dragInfo.current.id == null) return;
        const table = filteredTables.find(t => t.id === dragInfo.current.id);
        if (!table) return;
        const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
        // Divide by scale to convert screen pixels to canvas pixels
        let newX = (clientX - dragInfo.current.offsetX) / effectiveScale;
        let newY = (clientY - dragInfo.current.offsetY) / effectiveScale;
        // Clamp so table stays inside grid
        const minX = 0;
        const minY = 0;
        const maxX = effectiveGridWidth - (table.width + gridPadding);
        const maxY = effectiveGridHeight - (table.height + gridPadding);
        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));
        // Snap to grid if enabled
        const snap = gridSnapSize || 0;
        if (snap > 0 && snapEnabled) {
            newX = Math.round(newX / snap) * snap;
            newY = Math.round(newY / snap) * snap;
        }
        setPositions(prev => {
            const updated = {
                ...prev,
                [dragInfo.current.id]: { x: newX, y: newY }
            };
            dragInfo.current.latestPos = updated[dragInfo.current.id];
            return updated;
        });
        if (e.type === 'touchmove') e.preventDefault();
    }

    function handleDragEnd() {
        setShakingId(null);
        if (dragInfo.current.id != null && dragInfo.current.latestPos) {
            const { x, y } = dragInfo.current.latestPos;
            fetch(`${BACKEND_URL}/api/tables/${dragInfo.current.id}/position`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ x: Math.round(x), y: Math.round(y) })
            })
            .then(() => refreshTables())
            .catch(() => {});
        }
        dragInfo.current.id = null;
        dragInfo.current.latestPos = null;
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDrag);
        window.removeEventListener('touchend', handleDragEnd);
    }

    function handleHoldStart(e, table) {
        if (!movementEnabled || readOnly) return;

        dragTimeout.current = setTimeout(() => {
            dragInfo.current.id = table.id;
            setShakingId(table.id);
            const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
            const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
            dragInfo.current.offsetX = clientX - (positions[table.id]?.x || 0) * effectiveScale;
            dragInfo.current.offsetY = clientY - (positions[table.id]?.y || 0) * effectiveScale;
            window.addEventListener('mousemove', handleDrag);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleDrag, { passive: false });
            window.addEventListener('touchend', handleDragEnd);
            isDragging.current = true;
        }, 120);
    }

    function handleHoldEnd() {
        clearTimeout(dragTimeout.current);
        dragTimeout.current = null;
        if (!isDragging.current) {
            dragInfo.current.id = null;
        }
        setTimeout(() => {
            if (dragInfo.current.id != null && dragInfo.current.latestPos) {
                const { x, y } = dragInfo.current.latestPos;
                fetch(`${BACKEND_URL}/api/tables/${dragInfo.current.id}/position`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ x: Math.round(x), y: Math.round(y) })
                })
                .then(() => refreshTables())
                .catch(() => {});
            }
            isDragging.current = false;
            dragInfo.current.latestPos = null;
        }, 120);
    }

    async function handleSaveEditTable(updatedTable) {
        try {
            // eslint-disable-next-line no-unused-vars
            const res = await fetch(`${BACKEND_URL}/api/tables/${updatedTable.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedTable)
            });
            setEditingTable(null);
            await refreshTables();
        } catch (err) {
            console.error('Failed to save table edits', err);
            await refreshTables();
        }
    }

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleDrag);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDrag);
            window.removeEventListener('touchend', handleDragEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line no-unused-vars
    async function handleAddTable() {
        const usedNumbers = filteredTables
            .map(t => {
                const m = /^New Table(\d+)$/.exec(t.name);
                return m ? parseInt(m[1], 10) : null;
            })
            .filter(n => n !== null);
        let N = 1;
        while (usedNumbers.includes(N)) N++;
        const newTable = {
            name: `${t('newItem')} ${t('tableName')}${N}`,
            restaurant_id: 1,
            shape: 'rectangle',
            width: 40,
            height: 40,
            x: 100,
            y: 100,
            temporary: 0
        };
        try {
            const res = await fetch(`${BACKEND_URL}/api/tables`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newTable)
            });
            let errorMsg = 'Failed to create table';
            let backendMsg = '';
            if (!res.ok) {
                try {
                    const errJson = await res.json();
                    if (errJson && errJson.error) {
                        backendMsg = errJson.error;
                        if (errJson.details && Array.isArray(errJson.details.missingFields)) {
                            backendMsg += '\nMissing fields: ' + errJson.details.missingFields.join(', ');
                        } else if (errJson.details && typeof errJson.details === 'object' && errJson.details.sqlMessage) {
                            backendMsg += ': ' + errJson.details.sqlMessage;
                        }
                    }
                } catch {}
                throw new Error(backendMsg || errorMsg);
            }
            const created = await res.json();
            setEditingTable(created);
            await refreshTables();
        } catch (err) {
            alert('Failed to create table: ' + err.message);
        }
    }

    const resizeHandleColor = theme.colors.primary || '#1976d2';

    return (
        <>
            {!editingTable && !tableInfo && (
                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                    }}
                >
                    {/* Area filter - only where the tenant has more than one area */}
                    {tableAreasEnabled && areas.length > 1 && (
                        <div style={{
                            display: 'flex',
                            gap: '0.4rem',
                            flexWrap: 'wrap',
                            marginBottom: '0.5rem'
                        }}>
                            {[{ id: null, name: t('allAreas') }, ...areas].map(area => (
                                <button
                                    key={area.id === null ? 'all' : area.id}
                                    onClick={() => setSelectedAreaId(area.id)}
                                    style={{
                                        padding: '0.35rem 0.75rem',
                                        backgroundColor: selectedAreaId === area.id
                                            ? (theme.colors.primary || '#1976d2')
                                            : (theme.colors.surface || '#fff'),
                                        color: selectedAreaId === area.id
                                            ? (theme.colors.textLight || '#fff')
                                            : (theme.colors.text || '#000'),
                                        border: `1px solid ${theme.colors.border || '#ccc'}`,
                                        borderRadius: theme.borderRadius || 8,
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {area.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Outer wrapper: holds buttons (unscaled) + scaled grid + resize handles */}
                    <div style={{ position: 'relative', width: '100%' }}>

                        {/* Toggle buttons — OUTSIDE the scaled grid so they stay full size */}
                        {!readOnly && (
                            <div style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                zIndex: 200,
                                display: 'flex',
                                gap: '2px',
                            }}>
                                {movementEnabled && gridSnapSize > 0 && (
                                    <button
                                        onClick={() => setSnapEnabled(!snapEnabled)}
                                        title={snapEnabled ? t('snapToGrid') + ' ON' : t('snapToGrid') + ' OFF'}
                                        style={{
                                            padding: '2px 6px',
                                            backgroundColor: snapEnabled ? theme.colors.success : theme.colors.secondary,
                                            color: theme.colors.textLight,
                                            border: 'none',
                                            borderRadius: theme.borderRadius || 8,
                                            cursor: 'pointer',
                                            boxShadow: theme.boxShadow,
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            lineHeight: '1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '30px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        SNAP {snapEnabled ? 'ON' : 'OFF'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setMovementEnabled(!movementEnabled)}
                                    title={movementEnabled ? t('disableMovement') : t('enableMovement')}
                                    style={{
                                        padding: '0rem',
                                        backgroundColor: movementEnabled ? theme.colors.success : theme.colors.secondary,
                                        color: theme.colors.textLight,
                                        border: 'none',
                                        borderRadius: theme.borderRadius || 8,
                                        cursor: 'pointer',
                                        boxShadow: theme.boxShadow,
                                        fontSize: '1rem',
                                        lineHeight: '1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '30px',
                                        minHeight: '30px'
                                    }}
                                >
                                    {movementEnabled ? '🔓' : '🔒'}
                                </button>
                            </div>
                        )}

                        {/* Scaled container */}
                        <div
                            style={{
                                width: '100%',
                                height: effectiveScaledHeight,
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                className="table-display-grid"
                                style={{
                                    position: 'relative',
                                    width: effectiveGridWidth,
                                    height: effectiveGridHeight,
                                    boxSizing: 'border-box',
                                    transform: `scale(${effectiveScale})`,
                                    transformOrigin: 'top left',
                                    background: theme.colors.surface,
                                    backgroundImage: (movementEnabled && gridSnapSize > 0 && snapEnabled)
                                        ? `linear-gradient(to right, ${theme.colors.border || '#ccc'}22 1px, transparent 1px), linear-gradient(to bottom, ${theme.colors.border || '#ccc'}22 1px, transparent 1px)`
                                        : 'none',
                                    backgroundSize: (movementEnabled && gridSnapSize > 0 && snapEnabled)
                                        ? `${gridSnapSize}px ${gridSnapSize}px`
                                        : 'auto',
                                    color: theme.colors.text,
                                    border: `2px solid ${theme.colors.primary || '#1976d2'}`,
                                    borderRadius: theme?.borderRadius || 8,
                                    boxShadow: theme?.boxShadow || '0 2px 8px #0002',
                                    padding: '0.5rem',
                                }}
                            >
                                {filteredTables.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: theme.colors.textSecondary }}>
                                        {t('no')} {t('tables').toLowerCase()} {t('view').toLowerCase()}.
                                    </div>
                                ) : (
                                    (() => {
                                        const sortedTables = [...filteredTables].sort((a, b) => {
                                            if (a.y !== b.y) return a.y - b.y;
                                            return a.x - b.x;
                                        });
                                        return sortedTables;
                                    })().map((table) => {
                                        const hasOrder = orders.some(order => order.table_id === table.id && order.closed === 0);
                                        const padding = 8;
                                        const isRound = table.shape === 'round';
                                        const usableW = (isRound ? table.width * 0.7 : table.width) - padding * 2;
                                        const usableH = (isRound ? table.height * 0.7 : table.height) - padding * 2;
                                        const nameLen = (table.name || '').length || 1;
                                        const fitByWidth = usableW / (nameLen * 0.55);
                                        const fitByHeight = usableH / 2;
                                        const autoFontSize = Math.max(10, Math.min(fitByWidth, fitByHeight, 32));
                                        return (
                                            <div
                                                key={table.id}
                                                style={{
                                                    position: 'absolute',
                                                    left: positions[table.id]?.x || 0,
                                                    top: positions[table.id]?.y || 0,
                                                    width: table.width,
                                                    height: table.height,
                                                    background: hasOrder ? theme.colors.secondary : theme.colors.background,
                                                    border: `2px solid ${theme.colors.primary}`,
                                                    borderRadius: table.shape === 'round' ? '50%' : (table.shape === 'square' ? '4px' : theme.borderRadius),
                                                    boxShadow: theme.boxShadow,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: theme.colors.text,
                                                    fontWeight: 'bold',
                                                    fontSize: `${autoFontSize}px`,
                                                    overflow: 'hidden',
                                                    touchAction: movementEnabled ? 'none' : 'auto',
                                                    cursor: readOnly ? 'default' : (movementEnabled ? 'grab' : 'pointer'),
                                                    animation: shakingId === table.id ? 'shake 0.5s infinite' : 'none',
                                                    opacity: readOnly ? 0.9 : 1,
                                                    padding: `${padding}px`,
                                                    boxSizing: 'border-box',
                                                    textAlign: 'center',
                                                    lineHeight: 1.1,
                                                }}
                                                onMouseDown={readOnly ? undefined : (e => handleHoldStart(e, table))}
                                                onMouseUp={readOnly ? undefined : handleHoldEnd}
                                                onMouseLeave={readOnly ? undefined : handleHoldEnd}
                                                onTouchStart={readOnly ? undefined : (e => handleHoldStart(e, table))}
                                                onTouchEnd={readOnly ? undefined : handleHoldEnd}
                                                onDoubleClick={readOnly || movementEnabled ? undefined : (() => setEditingTable(table))}
                                                onClick={readOnly || movementEnabled ? undefined : (async () => {
                                                    if (!isDragging.current) {
                                                        const pos = positions[table.id];
                                                        if (pos) {
                                                            await fetch(`${BACKEND_URL}/api/tables/${table.id}/position`, {
                                                                method: 'PUT',
                                                                headers: { 'Content-Type': 'application/json' },
                                                                body: JSON.stringify({ x: pos.x, y: pos.y })
                                                            });
                                                            refreshTables();
                                                        }
                                                        setTableInfo(table);
                                                    }
                                                })}
                                            >
                                                <div style={{ whiteSpace: 'nowrap' }}>{table.name}</div>
                                                <div style={{ fontSize: '0.85em', color: theme.colors.textSecondary }}>{table.status}</div>
                                                <div style={{ fontSize: '0.75em' }}>{table.server}</div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Right edge resize handle */}
                        {movementEnabled && !readOnly && (
                            <div
                                onMouseDown={(e) => handleResizeStart(e, 'right')}
                                onTouchStart={(e) => handleResizeStart(e, 'right')}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '8px',
                                    height: effectiveScaledHeight,
                                    cursor: 'ew-resize',
                                    backgroundColor: resizingEdge === 'right' || resizingEdge === 'corner' ? resizeHandleColor : 'transparent',
                                    borderRight: `2px solid ${resizeHandleColor}44`,
                                    zIndex: 10,
                                    touchAction: 'none',
                                }}
                            />
                        )}

                        {/* Bottom edge resize handle */}
                        {movementEnabled && !readOnly && (
                            <div
                                onMouseDown={(e) => handleResizeStart(e, 'bottom')}
                                onTouchStart={(e) => handleResizeStart(e, 'bottom')}
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '8px',
                                    cursor: 'ns-resize',
                                    backgroundColor: resizingEdge === 'bottom' || resizingEdge === 'corner' ? resizeHandleColor : 'transparent',
                                    borderBottom: `2px solid ${resizeHandleColor}44`,
                                    zIndex: 10,
                                    touchAction: 'none',
                                }}
                            />
                        )}

                        {/* Corner resize handle */}
                        {movementEnabled && !readOnly && (
                            <div
                                onMouseDown={(e) => handleResizeStart(e, 'corner')}
                                onTouchStart={(e) => handleResizeStart(e, 'corner')}
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: '14px',
                                    height: '14px',
                                    cursor: 'nwse-resize',
                                    backgroundColor: resizingEdge === 'corner' ? resizeHandleColor : `${resizeHandleColor}44`,
                                    borderRadius: '2px',
                                    zIndex: 11,
                                    touchAction: 'none',
                                }}
                            />
                        )}

                        {/* Dimension label while resizing */}
                        {resizingEdge && (
                            <div style={{
                                position: 'absolute',
                                bottom: '-24px',
                                right: '0',
                                fontSize: '0.75rem',
                                color: theme.colors.textSecondary || '#666',
                                backgroundColor: theme.colors.surface || '#fff',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                border: `1px solid ${theme.colors.border || '#ccc'}`,
                                zIndex: 12,
                            }}>
                                {effectiveCanvasWidth} x {effectiveCanvasHeight}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {editingTable && (
                <TableEdit
                    table={editingTable}
                    onSave={handleSaveEditTable}
                    onCancel={() => setEditingTable(null)}
                    theme={theme}
                    minX={0}
                    minY={0}
                    maxX={effectiveGridWidth - (editingTable.width + gridPadding)}
                    maxY={effectiveGridHeight - (editingTable.height + gridPadding)}
                />
            )}

            {tableInfo && (
                <TableInfo
                    table={tableInfo}
                    onClose={() => {
                        setTableInfo(null);
                        refreshTables();
                    }}
                    theme={theme}
                    onEdit={table => {
                        setEditingTable(table);
                        setTableInfo(null);
                    }}
                    onOrderSelect={onOrderSelect}
                    user={user}
                />
            )}
        </>
    );
}

// Add shake animation CSS (only once, outside the function)
const style = document.createElement('style');
style.innerHTML = `@keyframes shake { 0% { transform: translate(0, 0); } 20% { transform: translate(-2px, 0); } 40% { transform: translate(2px, 0); } 60% { transform: translate(-2px, 0); } 80% { transform: translate(2px, 0); } 100% { transform: translate(0, 0); } }`;
document.head.appendChild(style);

export default TableDisplay;
